/**
 * DD-BE-PR2b-1/2: D&D workflow line/step + chat mutations + queue-state hydrate.
 * Gated by products_design.queue.view.
 */
import type { AxiosRequestConfig } from 'axios'
import { AxiosError } from 'axios'
import apiService from '@/services/api'
import type { SubjectMutation } from '@/lib/dd-queue/types/subjectMutation'
import {
  narrowPiqQuoteStatus,
  narrowPiqUnitPriceSource,
} from '@/lib/dd-queue/types/subjectMutation'
import type {
  ActivityEntry,
  AssigneeLogEntry,
  ChatMessage,
  DeliverableEntry,
  DeliverableStatus,
  EstimationMode,
  PimMode,
} from '@/lib/dd-queue/types'
import { STEPS_BY_KEY } from '@/lib/dd-queue/data/steps'
import { formatActivityTime } from '@/lib/dd-queue/activityLog'

const STRICT_SUCCESS_HTTP: AxiosRequestConfig = {
  validateStatus: (status) => status >= 200 && status < 300,
}

/** Accept 2xx and 409 so we can read conflict snapshots before rejectIfClientError. */
const ALLOW_CONFLICT_HTTP: AxiosRequestConfig = {
  validateStatus: (status) => (status >= 200 && status < 300) || status === 409,
}

export interface DdWorkflowStintSnapshot {
  assignee: string
  assignee_user_id: number | null
  started_at: string | null
  ended_at: string | null
  accumulated_ms: number
}

export interface DdWorkflowStepSnapshot {
  step_key: string
  status: string
  assignee: string | null
  assignee_user_id: number | null
  opened_at: string | null
  completed_at: string | null
  /** STEP TIMER accumulated ms (PR2b-3). */
  timer_ms?: number
  /** STEP TIMER running stamp ISO (PR2b-3). */
  timer_started_at?: string | null
  version: number
  /** USER STINT rows (PR2b-3). */
  stints?: DdWorkflowStintSnapshot[]
}

export interface DdWorkflowActivitySnapshot {
  occurred_at: string | null
  actor_name: string
  event_text: string
  meta: Record<string, unknown> | null
}

/** Flat chat row from POST chat / queue-state (roots + replies). */
export interface DdWorkflowChatMessageSnapshot {
  uuid: string
  client_message_id: string
  parent_uuid: string | null
  author_user_id: number | null
  author_name: string
  text: string
  mentions: string[] | null
  /** DD-SALESWAIT-003 — surface that authored the message. */
  source?: 'dd' | 'sales' | 'hubspot'
  occurred_at: string
}

export interface DdWorkflowLineSnapshot {
  submission_uuid: string
  line_key: string
  version: number
  deleted_at: string | null
  deleted_by: string | null
  deleted_reason: string | null
  quoted_proof_id: string | null
  /** Packet meta (PR2b-3). */
  pms_colors?: string[] | null
  packet_notes?: string | null
  /** ROUTING-E1 — PIQ quote commercial fields (integer cents). */
  quote_number?: string | null
  unit_price_cents?: number | null
  tax_rate?: number | null
  payment_terms?: string | null
  quote_url?: string | null
  /** HS-DEAL-ATTACH-PARSE-002 */
  piq_quote_status?: string | null
  piq_unit_price_source?: string | null
  /** ROUTING-E3 — HubSpot ticket/deal record-only. */
  hubspot_ticket_id?: string | null
  hubspot_ticket_stage?: string | null
  /** HS-SYNC-B-001 — resolved pipeline stage label (computed). */
  hubspot_ticket_stage_label?: string | null
  hubspot_ticket_stage_at?: string | null
  hubspot_deal_id?: string | null
  hubspot_deal_stage?: string | null
  /** Edit Line (BUG-STG-006-FIX-2). */
  name_override?: string | null
  line_attributes?: Record<string, string> | null
  estimation_mode: string | null
  /** NP-TICKET-PER-SUBMISSION-001 — submission-wide routing SoT. */
  submission_estimation_mode?: EstimationMode | null
  pim_mode: string | null
  install_required: boolean | null
  steps: DdWorkflowStepSnapshot[]
  activity: DdWorkflowActivitySnapshot[]
  due_at?: string | null
  /** Present on PR2b-2+ snapshots; flat, occurred_at ASC, capped at 100. */
  chat?: DdWorkflowChatMessageSnapshot[]
}

interface SnapshotEnvelope {
  success: boolean
  data: DdWorkflowLineSnapshot
  message?: string
}

interface ChatMessageEnvelope {
  success: boolean
  data: DdWorkflowChatMessageSnapshot
  message?: string
}

/** GET queue-state returns `{ [submissionUuid]: LineSnapshot[] }`. */
interface QueueStateEnvelope {
  success: boolean
  data: Record<string, DdWorkflowLineSnapshot[]>
}

export class DdWorkflowConflictError extends Error {
  readonly snapshot: DdWorkflowLineSnapshot

  constructor(message: string, snapshot: DdWorkflowLineSnapshot) {
    super(message)
    this.name = 'DdWorkflowConflictError'
    this.snapshot = snapshot
  }
}

function lineBase(submissionUuid: string, lineKey: string): string {
  return `/v1/design-development/submissions/${encodeURIComponent(submissionUuid)}/lines/${encodeURIComponent(lineKey)}`
}

function snapshotFromAxiosError(err: unknown): DdWorkflowLineSnapshot | null {
  if (!(err instanceof AxiosError)) return null
  const data = err.response?.data as SnapshotEnvelope | undefined
  if (err.response?.status === 409 && data?.data && typeof data.data === 'object') {
    return data.data
  }
  return null
}

function unwrapSnapshot(response: unknown, fallbackMessage: string): DdWorkflowLineSnapshot {
  const envelope = response as SnapshotEnvelope
  if (!envelope?.success || !envelope.data) {
    throw new Error(envelope?.message || fallbackMessage)
  }
  return envelope.data
}

function messageFromAxiosError(err: unknown, fallback: string): string {
  if (err instanceof AxiosError) {
    const data = err.response?.data as { message?: string } | undefined
    if (typeof data?.message === 'string' && data.message.trim() !== '') {
      return data.message
    }
    if (typeof err.message === 'string' && err.message.trim() !== '') {
      return err.message
    }
  }
  if (err instanceof Error && err.message.trim() !== '') return err.message
  return fallback
}

async function mutateWithConflict(
  run: () => Promise<unknown>,
  fallbackMessage: string
): Promise<DdWorkflowLineSnapshot> {
  try {
    return unwrapSnapshot(await run(), fallbackMessage)
  } catch (err) {
    if (err instanceof DdWorkflowConflictError) throw err
    const snap = snapshotFromAxiosError(err)
    if (snap) {
      throw new DdWorkflowConflictError(
        messageFromAxiosError(err, 'Someone else updated this step'),
        snap
      )
    }
    throw new Error(messageFromAxiosError(err, fallbackMessage))
  }
}

export async function fetchDdQueueState(
  submissionUuids: string[]
): Promise<DdWorkflowLineSnapshot[]> {
  if (submissionUuids.length === 0) return []
  const response = await apiService.get<QueueStateEnvelope>(
    '/v1/design-development/queue-state',
    {
      params: { submission_uuids: submissionUuids },
      ...STRICT_SUCCESS_HTTP,
    }
  )
  if (!response?.success || !response.data || typeof response.data !== 'object') {
    throw new Error('Failed to load D&D workflow state')
  }
  const flat: DdWorkflowLineSnapshot[] = []
  for (const lines of Object.values(response.data)) {
    if (Array.isArray(lines)) flat.push(...lines)
  }
  return flat
}

export async function putDdWorkflowAssignment(args: {
  submissionUuid: string
  lineKey: string
  stepKey: string
  assignee: string | null
  expectedVersion: number | null
}): Promise<DdWorkflowLineSnapshot> {
  return mutateWithConflict(
    () =>
      apiService.put(
        `${lineBase(args.submissionUuid, args.lineKey)}/steps/${encodeURIComponent(args.stepKey)}/assignment`,
        {
          assignee: args.assignee,
          expected_version: args.expectedVersion,
        },
        ALLOW_CONFLICT_HTTP
      ),
    'Assignment failed'
  )
}

export async function putDdWorkflowEstimationMode(args: {
  submissionUuid: string
  estimationMode: string
}): Promise<{ submission_uuid: string; estimation_mode: string }> {
  const res = await apiService.put(
    `/v1/design-development/submissions/${encodeURIComponent(args.submissionUuid)}/estimation-mode`,
    { estimation_mode: args.estimationMode },
  )
  const body = res as {
    success?: boolean
    message?: string
    code?: string
    data?: { submission_uuid: string; estimation_mode: string }
  }
  // apiService.put returns response.data and throws AxiosError with Laravel message on 4xx.
  if (!body?.success || !body.data) {
    throw new Error(body?.message || 'Failed to set routing')
  }
  return body.data
}

export async function postDdWorkflowComplete(args: {
  submissionUuid: string
  lineKey: string
  stepKey: string
  expectedVersion: number | null
  force?: boolean
  quotedProofId?: string | null
  estimationMode?: string | null
  installRequired?: boolean | null
  deliverables?: Record<string, boolean> | null
}): Promise<DdWorkflowLineSnapshot> {
  return mutateWithConflict(
    () =>
      apiService.post(
        `${lineBase(args.submissionUuid, args.lineKey)}/steps/${encodeURIComponent(args.stepKey)}/complete`,
        {
          expected_version: args.expectedVersion,
          ...(args.force ? { force: true } : {}),
          ...(args.quotedProofId != null ? { quoted_proof_id: args.quotedProofId } : {}),
          ...(args.estimationMode != null ? { estimation_mode: args.estimationMode } : {}),
          ...(args.installRequired != null ? { install_required: args.installRequired } : {}),
          ...(args.deliverables != null ? { deliverables: args.deliverables } : {}),
        },
        ALLOW_CONFLICT_HTTP
      ),
    'Complete failed'
  )
}

export async function postDdWorkflowTimerStart(args: {
  submissionUuid: string
  lineKey: string
  stepKey: string
  expectedVersion: number | null
}): Promise<DdWorkflowLineSnapshot> {
  return mutateWithConflict(
    () =>
      apiService.post(
        `${lineBase(args.submissionUuid, args.lineKey)}/steps/${encodeURIComponent(args.stepKey)}/timer/start`,
        { expected_version: args.expectedVersion },
        ALLOW_CONFLICT_HTTP
      ),
    'Timer start failed'
  )
}

export async function postDdWorkflowTimerStop(args: {
  submissionUuid: string
  lineKey: string
  stepKey: string
  expectedVersion: number | null
}): Promise<DdWorkflowLineSnapshot> {
  return mutateWithConflict(
    () =>
      apiService.post(
        `${lineBase(args.submissionUuid, args.lineKey)}/steps/${encodeURIComponent(args.stepKey)}/timer/stop`,
        { expected_version: args.expectedVersion },
        ALLOW_CONFLICT_HTTP
      ),
    'Timer stop failed'
  )
}

export async function putDdWorkflowPacket(args: {
  submissionUuid: string
  lineKey: string
  expectedVersion: number | null
  pmsColors?: string[] | null
  packetNotes?: string | null
  quotedProofId?: string | null
  quoteNumber?: string | null
  unitPriceCents?: number | null
  taxRate?: number | null
  paymentTerms?: string | null
  quoteUrl?: string | null
  hubspotTicketId?: string | null
  hubspotTicketStage?: string | null
  hubspotDealId?: string | null
  hubspotDealStage?: string | null
}): Promise<DdWorkflowLineSnapshot> {
  const body: Record<string, unknown> = {
    expected_version: args.expectedVersion,
  }
  if (args.pmsColors !== undefined) body.pms_colors = args.pmsColors
  if (args.packetNotes !== undefined) body.packet_notes = args.packetNotes
  if (args.quotedProofId !== undefined) body.quoted_proof_id = args.quotedProofId
  if (args.quoteNumber !== undefined) body.quote_number = args.quoteNumber
  if (args.unitPriceCents !== undefined) body.unit_price_cents = args.unitPriceCents
  if (args.taxRate !== undefined) body.tax_rate = args.taxRate
  if (args.paymentTerms !== undefined) body.payment_terms = args.paymentTerms
  if (args.quoteUrl !== undefined) body.quote_url = args.quoteUrl
  if (args.hubspotTicketId !== undefined) body.hubspot_ticket_id = args.hubspotTicketId
  if (args.hubspotTicketStage !== undefined) body.hubspot_ticket_stage = args.hubspotTicketStage
  if (args.hubspotDealId !== undefined) body.hubspot_deal_id = args.hubspotDealId
  if (args.hubspotDealStage !== undefined) body.hubspot_deal_stage = args.hubspotDealStage

  return mutateWithConflict(
    () => apiService.put(`${lineBase(args.submissionUuid, args.lineKey)}/packet`, body, ALLOW_CONFLICT_HTTP),
    'Packet update failed'
  )
}

/** BUG-STG-006-FIX-2 — Edit Line name + attributes. */
export async function putDdWorkflowLineEditor(args: {
  submissionUuid: string
  lineKey: string
  expectedVersion: number | null
  nameOverride?: string | null
  lineAttributes?: Record<string, string> | null
}): Promise<DdWorkflowLineSnapshot> {
  const body: Record<string, unknown> = {
    expected_version: args.expectedVersion,
  }
  if (args.nameOverride !== undefined) body.name_override = args.nameOverride
  if (args.lineAttributes !== undefined) body.line_attributes = args.lineAttributes

  return mutateWithConflict(
    () => apiService.put(`${lineBase(args.submissionUuid, args.lineKey)}/editor`, body, ALLOW_CONFLICT_HTTP),
    'Line editor save failed'
  )
}

export async function postDdWorkflowReactivate(args: {
  submissionUuid: string
  lineKey: string
  stepKey: string
  expectedVersion: number | null
}): Promise<DdWorkflowLineSnapshot> {
  return mutateWithConflict(
    () =>
      apiService.post(
        `${lineBase(args.submissionUuid, args.lineKey)}/steps/${encodeURIComponent(args.stepKey)}/reactivate`,
        { expected_version: args.expectedVersion },
        ALLOW_CONFLICT_HTTP
      ),
    'Reactivate failed'
  )
}

export async function postDdWorkflowCancel(args: {
  submissionUuid: string
  lineKey: string
  reason: string
  expectedVersion: number | null
}): Promise<DdWorkflowLineSnapshot> {
  return mutateWithConflict(
    () =>
      apiService.post(
        `${lineBase(args.submissionUuid, args.lineKey)}/cancel`,
        {
          reason: args.reason,
          expected_version: args.expectedVersion,
        },
        ALLOW_CONFLICT_HTTP
      ),
    'Cancel failed'
  )
}

export async function postDdWorkflowRestore(args: {
  submissionUuid: string
  lineKey: string
  expectedVersion: number | null
}): Promise<DdWorkflowLineSnapshot> {
  return mutateWithConflict(
    () =>
      apiService.post(
        `${lineBase(args.submissionUuid, args.lineKey)}/restore`,
        { expected_version: args.expectedVersion },
        ALLOW_CONFLICT_HTTP
      ),
    'Restore failed'
  )
}

function unwrapChatMessage(response: unknown, fallbackMessage: string): DdWorkflowChatMessageSnapshot {
  const envelope = response as ChatMessageEnvelope
  if (!envelope?.success || !envelope.data) {
    throw new Error(envelope?.message || fallbackMessage)
  }
  return envelope.data
}

export async function postDdWorkflowChat(args: {
  submissionUuid: string
  lineKey: string
  id: string
  text: string
  mentions?: string[]
}): Promise<DdWorkflowChatMessageSnapshot> {
  const response = await apiService.post(
    `${lineBase(args.submissionUuid, args.lineKey)}/chat`,
    {
      id: args.id,
      text: args.text,
      ...(args.mentions?.length ? { mentions: args.mentions } : {}),
    },
    STRICT_SUCCESS_HTTP
  )
  return unwrapChatMessage(response, 'Chat post failed')
}

export async function postDdWorkflowChatReply(args: {
  submissionUuid: string
  lineKey: string
  parentUuid: string
  id: string
  text: string
  mentions?: string[]
}): Promise<DdWorkflowChatMessageSnapshot> {
  const response = await apiService.post(
    `${lineBase(args.submissionUuid, args.lineKey)}/chat/${encodeURIComponent(args.parentUuid)}/replies`,
    {
      id: args.id,
      text: args.text,
      ...(args.mentions?.length ? { mentions: args.mentions } : {}),
    },
    STRICT_SUCCESS_HTTP
  )
  return unwrapChatMessage(response, 'Chat reply failed')
}

export function chatMessageFromSnapshot(snap: DdWorkflowChatMessageSnapshot): ChatMessage {
  const ts = Date.parse(snap.occurred_at)
  const finite = Number.isFinite(ts)
  return {
    id: snap.client_message_id,
    uuid: snap.uuid,
    author: snap.author_name,
    text: snap.text,
    t: finite ? new Date(ts).toLocaleString() : 'just now',
    ts: finite ? ts : Date.now(),
    ...(snap.mentions?.length ? { mentions: snap.mentions } : {}),
    ...(snap.source ? { source: snap.source } : {}),
  }
}

/** Split flat server chat into mutation overlay roots + replies-by-parent-client-id. */
export function chatSnapshotsToMutationPatch(
  messages: DdWorkflowChatMessageSnapshot[]
): Pick<SubjectMutation, 'chat' | 'chatReplies'> {
  const byUuid = new Map<string, DdWorkflowChatMessageSnapshot>()
  for (const m of messages) byUuid.set(m.uuid, m)

  const chat: ChatMessage[] = []
  const chatReplies: Record<string, ChatMessage[]> = {}

  for (const m of messages) {
    const mapped = chatMessageFromSnapshot(m)
    if (!m.parent_uuid) {
      chat.push(mapped)
      continue
    }
    const parent = byUuid.get(m.parent_uuid)
    const parentKey = parent?.client_message_id ?? m.parent_uuid
    chatReplies[parentKey] = [...(chatReplies[parentKey] ?? []), mapped]
  }

  return { chat, chatReplies }
}

function stintsToAssigneeLogs(stints: DdWorkflowStintSnapshot[] | undefined): AssigneeLogEntry[] {
  if (!stints?.length) return []
  const logs: AssigneeLogEntry[] = []
  for (const s of stints) {
    if (!s.started_at) continue
    const from = Date.parse(s.started_at)
    if (!Number.isFinite(from)) continue
    logs.push({
      assignee: s.assignee,
      from,
      to: s.ended_at ? Date.parse(s.ended_at) : null,
    })
  }
  return logs
}

/**
 * Map a server line snapshot onto SubjectMutation overlay fields.
 * Files stay non-destructive. Chat replaces only when `replaceChat` (hydrate).
 * PR2b-3: timers, stints, packet meta replace from snapshot (server SoT).
 */
export function workflowSnapshotToMutationPatch(
  snapshot: DdWorkflowLineSnapshot,
  existing?: SubjectMutation,
  opts?: { replaceChat?: boolean }
): SubjectMutation {
  const deliverableUpdates: Record<string, Partial<DeliverableEntry>> = {
    ...(existing?.deliverableUpdates ?? {}),
  }
  const deliverableOpenedAt: Record<string, number> = {
    ...(existing?.deliverableOpenedAt ?? {}),
  }
  const deliverableCompletedAt: Record<string, number> = {
    ...(existing?.deliverableCompletedAt ?? {}),
  }
  const deliverableAssigneeLogs: Record<string, AssigneeLogEntry[]> = {
    ...(existing?.deliverableAssigneeLogs ?? {}),
  }
  const stepTimers: Record<string, { ms: number; startedAt: number | null }> = {
    ...(existing?.stepTimers ?? {}),
  }
  const workflowStepVersions: Record<string, number> = {
    ...(existing?.workflowStepVersions ?? {}),
  }

  for (const step of snapshot.steps) {
    // DD-GENERATE-ORDER-003 — ignore orphan DB step keys removed from STEP_LIBRARY.
    if (!STEPS_BY_KEY[step.step_key]) continue

    const status = step.status as DeliverableStatus
    deliverableUpdates[step.step_key] = {
      status,
      assignee: step.assignee,
    }
    workflowStepVersions[step.step_key] = step.version
    if (step.opened_at) {
      deliverableOpenedAt[step.step_key] = Date.parse(step.opened_at)
    } else {
      delete deliverableOpenedAt[step.step_key]
    }
    if (step.completed_at) {
      deliverableCompletedAt[step.step_key] = Date.parse(step.completed_at)
    } else {
      delete deliverableCompletedAt[step.step_key]
    }

    const timerMs = step.timer_ms ?? 0
    const startedRaw = step.timer_started_at ?? null
    const startedAt =
      startedRaw != null ? Date.parse(startedRaw) : null
    stepTimers[step.step_key] = {
      ms: timerMs,
      startedAt: startedAt != null && Number.isFinite(startedAt) ? startedAt : null,
    }

    if (Array.isArray(step.stints)) {
      deliverableAssigneeLogs[step.step_key] = stintsToAssigneeLogs(step.stints)
    }
  }

  const activityHead: ActivityEntry[] = snapshot.activity.map((a) => ({
    t: a.occurred_at ? formatActivityTime(a.occurred_at) : 'just now',
    e: a.event_text,
  }))

  const designTimer = stepTimers.design

  const patch: SubjectMutation = {
    ...existing,
    deliverableUpdates,
    deliverableOpenedAt,
    deliverableCompletedAt,
    deliverableAssigneeLogs,
    stepTimers,
    activityHead,
    workflowLineVersion: snapshot.version,
    workflowStepVersions,
  }

  if (designTimer) {
    patch.designTimerMs = designTimer.ms
    patch.designTimerStartedAt = designTimer.startedAt
  }

  if (snapshot.deleted_at) {
    patch.deletedAt = snapshot.deleted_at
    patch.deletedBy = snapshot.deleted_by ?? undefined
    patch.deletedReason = snapshot.deleted_reason ?? undefined
  } else {
    delete patch.deletedAt
    delete patch.deletedBy
    delete patch.deletedReason
  }

  if (snapshot.quoted_proof_id) {
    patch.quotedProofId = snapshot.quoted_proof_id
  } else {
    delete patch.quotedProofId
  }

  if (Array.isArray(snapshot.pms_colors)) {
    patch.pmsColors = snapshot.pms_colors
  } else if (snapshot.pms_colors === null) {
    delete patch.pmsColors
  }

  if (typeof snapshot.packet_notes === 'string') {
    patch.packetNotes = snapshot.packet_notes
  } else if (snapshot.packet_notes === null) {
    delete patch.packetNotes
  }

  if (snapshot.quote_number !== undefined) {
    patch.quoteNumber =
      typeof snapshot.quote_number === 'string' && snapshot.quote_number.length > 0
        ? snapshot.quote_number
        : null
  }

  if (snapshot.unit_price_cents !== undefined) {
    if (snapshot.unit_price_cents === null) {
      patch.unitPriceCents = null
    } else if (typeof snapshot.unit_price_cents === 'number') {
      patch.unitPriceCents = Math.trunc(snapshot.unit_price_cents)
    }
  }

  if (snapshot.tax_rate !== undefined) {
    patch.taxRate = snapshot.tax_rate
  }

  if (snapshot.payment_terms !== undefined) {
    patch.paymentTerms =
      typeof snapshot.payment_terms === 'string' && snapshot.payment_terms.length > 0
        ? snapshot.payment_terms
        : null
  }

  if (snapshot.quote_url !== undefined) {
    patch.quoteUrl =
      typeof snapshot.quote_url === 'string' && snapshot.quote_url.length > 0
        ? snapshot.quote_url
        : null
  }

  if (snapshot.piq_quote_status !== undefined) {
    patch.piqQuoteStatus = narrowPiqQuoteStatus(snapshot.piq_quote_status)
  }
  if (snapshot.piq_unit_price_source !== undefined) {
    patch.piqUnitPriceSource = narrowPiqUnitPriceSource(snapshot.piq_unit_price_source)
  }

  if (snapshot.hubspot_ticket_id !== undefined) {
    patch.hubspotTicketId =
      typeof snapshot.hubspot_ticket_id === 'string' && snapshot.hubspot_ticket_id.length > 0
        ? snapshot.hubspot_ticket_id
        : undefined
    if (!patch.hubspotTicketId) delete patch.hubspotTicketId
  }
  if (snapshot.hubspot_ticket_stage !== undefined) {
    patch.hubspotTicketStage =
      typeof snapshot.hubspot_ticket_stage === 'string' &&
      snapshot.hubspot_ticket_stage.length > 0
        ? snapshot.hubspot_ticket_stage
        : undefined
    if (!patch.hubspotTicketStage) delete patch.hubspotTicketStage
  }
  if (snapshot.hubspot_ticket_stage_label !== undefined) {
    patch.hubspotTicketStageLabel =
      typeof snapshot.hubspot_ticket_stage_label === 'string' &&
      snapshot.hubspot_ticket_stage_label.length > 0
        ? snapshot.hubspot_ticket_stage_label
        : undefined
    if (!patch.hubspotTicketStageLabel) delete patch.hubspotTicketStageLabel
  }
  if (snapshot.hubspot_deal_id !== undefined) {
    patch.hubspotDealId =
      typeof snapshot.hubspot_deal_id === 'string' && snapshot.hubspot_deal_id.length > 0
        ? snapshot.hubspot_deal_id
        : undefined
    if (!patch.hubspotDealId) delete patch.hubspotDealId
  }
  if (snapshot.hubspot_deal_stage !== undefined) {
    patch.hubspotDealStage =
      typeof snapshot.hubspot_deal_stage === 'string' && snapshot.hubspot_deal_stage.length > 0
        ? snapshot.hubspot_deal_stage
        : undefined
    if (!patch.hubspotDealStage) delete patch.hubspotDealStage
  }

  if (typeof snapshot.name_override === 'string' && snapshot.name_override.length > 0) {
    patch.nameOverride = snapshot.name_override
  } else if (snapshot.name_override === null || snapshot.name_override === '') {
    delete patch.nameOverride
  }

  if (snapshot.line_attributes && typeof snapshot.line_attributes === 'object') {
    patch.attributes = { ...snapshot.line_attributes }
  } else if (snapshot.line_attributes === null) {
    delete patch.attributes
  }

  if (snapshot.estimation_mode) {
    patch.estimationMode = snapshot.estimation_mode as EstimationMode
  }
  if (snapshot.pim_mode) {
    patch.pimMode = snapshot.pim_mode as PimMode
  }
  if (snapshot.install_required != null) {
    patch.installRequired = snapshot.install_required
  }

  if (opts?.replaceChat && Array.isArray(snapshot.chat)) {
    const { chat, chatReplies } = chatSnapshotsToMutationPatch(snapshot.chat)
    patch.chat = chat
    patch.chatReplies = chatReplies
  }

  // A1: line.due_at always wins once present (never leave submission ingest stamp).
  if (snapshot.due_at) {
    patch.dueAtIso = snapshot.due_at
  }

  return patch
}

/** True when the line has no useful persisted workflow state (SPA keeps seed-only). */
export function isEmptyWorkflowSnapshot(snapshot: DdWorkflowLineSnapshot): boolean {
  const hasChat = Array.isArray(snapshot.chat) && snapshot.chat.length > 0
  const hasPacket =
    (Array.isArray(snapshot.pms_colors) && snapshot.pms_colors.length > 0) ||
    (typeof snapshot.packet_notes === 'string' && snapshot.packet_notes.length > 0) ||
    Boolean(snapshot.quoted_proof_id) ||
    Boolean(snapshot.quote_number) ||
    snapshot.unit_price_cents != null ||
    snapshot.tax_rate != null ||
    Boolean(snapshot.payment_terms) ||
    Boolean(snapshot.quote_url) ||
    Boolean(snapshot.piq_quote_status) ||
    Boolean(snapshot.hubspot_ticket_id) ||
    Boolean(snapshot.hubspot_ticket_stage) ||
    Boolean(snapshot.hubspot_deal_id) ||
    Boolean(snapshot.hubspot_deal_stage)
  const hasEditor =
    (typeof snapshot.name_override === 'string' && snapshot.name_override.length > 0) ||
    (snapshot.line_attributes != null &&
      typeof snapshot.line_attributes === 'object' &&
      Object.keys(snapshot.line_attributes).length > 0)
  const hasDue = Boolean(snapshot.due_at)
  return (
    snapshot.steps.length === 0 &&
    !snapshot.deleted_at &&
    !hasChat &&
    !hasPacket &&
    !hasEditor &&
    !hasDue
  )
}
